import { useEffect, useState, type FormEvent } from 'react';
import Modal from './Modal';
import { normalizeAddress, shortAddress } from '../wallet/address';
import { installSuggestions, walletProfile, type WalletProfile } from '../wallet/compat';
import { GHOST_QUICK_PICKS, useGhost, useWallet } from '../wallet/context';
import { loadLastWallet } from '../wallet/storage';
import Msg from '../i18n/Msg';
import { useT } from '../i18n/useT';

function metamaskDeepLink(): string {
  const { host, pathname, search } = window.location;
  return `https://metamask.app.link/dapp/${host}${pathname}${search}`;
}

function SupportBadge({ profile }: { profile: WalletProfile }) {
  const t = useT();
  if (profile.support === 'unsupported') {
    return <span className="rounded bg-danger/15 px-1.5 py-px text-[10px] font-semibold uppercase text-danger">{t('wallet.picker.view_only')}</span>;
  }
  if (profile.support === 'caveat') {
    return <span className="rounded bg-warn/15 px-1.5 py-px text-[10px] font-semibold uppercase text-warn">{t('wallet.picker.caveat')}</span>;
  }
  return null;
}

/**
 * Wallet picker (#687): every EIP-6963 wallet with its own icon + name (MetaMask and Rabby first), a support badge
 * from wallet/compat.ts, the unsupported-wallet warning before the user tries, install links when nothing is
 * detected, and the ghost-mode entry.
 */
export function WalletPickerModal({
  open,
  onClose,
  onGhost,
}: {
  open: boolean;
  onClose: () => void;
  onGhost: () => void;
}) {
  const { wallets, connect, status, error, refreshWallets, clearError } = useWallet();
  const t = useT();
  const [pending, setPending] = useState<string | null>(null);
  const [lastUsed, setLastUsed] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      clearError();
      refreshWallets();
      setLastUsed(loadLastWallet());
    }
  }, [open, refreshWallets, clearError]);

  const pick = async (rdns: string) => {
    setPending(rdns);
    const address = await connect(rdns);
    setPending(null);
    if (address) onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={t('wallet.picker.title')}>
      {wallets.length > 0 ? (
        <ul className="flex flex-col gap-2" aria-label={t('wallet.picker.list')}>
          {wallets.map((w) => {
            const profile = walletProfile(w.info.rdns, w.provider);
            const noteId = `wallet-note-${w.info.uuid}`;
            const showNote = profile.support !== 'supported' && profile.note;
            return (
              <li key={w.info.uuid} data-testid="wallet-option" data-rdns={w.info.rdns} data-support={profile.support}>
                <button
                  type="button"
                  disabled={status === 'connecting'}
                  onClick={() => void pick(w.info.rdns)}
                  aria-describedby={showNote ? noteId : undefined}
                  className={`flex w-full items-center gap-3 rounded-lg border bg-surface-2 px-3 py-3 text-start text-sm font-medium hover:border-accent disabled:opacity-60 ${
                    profile.support === 'unsupported' ? 'border-danger/40' : 'border-border'
                  }`}
                >
                  <img src={w.info.icon} alt="" className="h-7 w-7 shrink-0 rounded" />
                  <span className="flex min-w-0 flex-1 items-center gap-2">
                    <span className="truncate">{w.info.name}</span>
                    <SupportBadge profile={profile} />
                  </span>
                  <span className="shrink-0 text-xs text-muted">
                    {pending === w.info.rdns
                      ? t('wallet.picker.check_wallet')
                      : lastUsed === w.info.rdns
                        ? t('wallet.picker.last_used')
                        : t('wallet.picker.detected')}
                  </span>
                </button>
                {showNote && (
                  <p
                    id={noteId}
                    role={profile.support === 'unsupported' ? 'note' : undefined}
                    className={`mt-1 px-1 text-xs ${profile.support === 'unsupported' ? 'text-danger' : 'text-muted'}`}
                  >
                    {profile.note}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="rounded-lg border border-border bg-surface-2 p-3 text-sm" data-testid="no-wallet">
          <p className="font-medium">{t('wallet.picker.none_title')}</p>
          <p className="mt-1 text-muted">{t('wallet.picker.none_body')}</p>
          <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1" aria-label={t('wallet.picker.install_list')}>
            {installSuggestions().map((p) => (
              <li key={p.id}>
                <a href={p.installUrl} target="_blank" rel="noreferrer" className="text-accent underline">
                  {t('wallet.picker.install', { wallet: p.name })}
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-muted">
            <Msg
              k="wallet.picker.phone"
              args={{
                link: (
                  <a href={metamaskDeepLink()} className="text-accent underline">
                    {t('wallet.picker.metamask_browser')}
                  </a>
                ),
              }}
            />
          </p>
        </div>
      )}
      {error && (
        <p role="status" className="mt-3 rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}
      <div className="mt-4 border-t border-border pt-3 text-sm text-muted">
        {t('wallet.picker.just_looking')}{' '}
        <button type="button" onClick={onGhost} className="font-medium text-accent hover:underline">
          {t('wallet.picker.watch')}
        </button>
      </div>
    </Modal>
  );
}

export function GhostDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { ghostAddress, enterGhost, exitGhost } = useGhost();
  const t = useT();
  const [input, setInput] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setInput('');
      setTouched(false);
    }
  }, [open]);

  const parsed = normalizeAddress(input);
  const invalid = touched && input.trim() !== '' && !parsed;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (enterGhost(input)) onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={t('wallet.ghost.title')}>
      <p className="mb-3 text-sm text-muted">
        {t('wallet.ghost.body')}
      </p>
      <form onSubmit={submit} noValidate>
        <label htmlFor="ghost-address" className="mb-1 block text-xs font-medium text-muted">
          {t('wallet.ghost.address_label')}
        </label>
        <input
          id="ghost-address"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder="0x…"
          spellCheck={false}
          autoCapitalize="off"
          autoComplete="off"
          aria-invalid={invalid}
          aria-describedby="ghost-address-hint"
          className="w-full rounded-lg border border-border bg-surface-2 px-3 py-2 font-mono text-sm outline-none focus:border-accent"
        />
        <p id="ghost-address-hint" className={`mt-1 min-h-[1.25rem] break-all text-xs ${invalid ? 'text-danger' : 'text-muted'}`}>
          {invalid ? t('wallet.ghost.invalid') : parsed ? parsed : ''}
        </p>
        <button
          type="submit"
          className="mt-2 w-full rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-bg disabled:opacity-60"
        >
          {t('wallet.ghost.watch')}
        </button>
      </form>
      <div className="mt-4">
        <p className="mb-2 text-xs font-medium text-muted">{t('wallet.ghost.quick_picks')}</p>
        <ul className="flex flex-col gap-1.5">
          {GHOST_QUICK_PICKS.map((q) => (
            <li key={q.address}>
              <button
                type="button"
                onClick={() => {
                  if (enterGhost(q.address)) onClose();
                }}
                className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-start text-sm hover:bg-surface-2"
              >
                <span className="font-mono">{shortAddress(q.address)}</span>
                <span className="truncate text-xs text-muted">{q.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      {ghostAddress && (
        <button
          type="button"
          onClick={() => {
            exitGhost();
            onClose();
          }}
          className="mt-4 w-full rounded-lg border border-warn px-3 py-2 text-sm font-semibold text-warn"
        >
          {t('wallet.exit_ghost')}
        </button>
      )}
    </Modal>
  );
}
